import { Button } from "@/Button";
import { LayoutHeader } from "@/Header";
import { ValidateInput } from "@/Input";
import { SequentialAnimation } from "@/SequentialAnimation";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { Controller, useForm } from "react-hook-form";
import { useMemo, useState } from "react";
import { ValidateUploader } from "@/Uploader";
import { ValidateSelectList } from "@/SelectList";
import { ValidateTextarea } from "@/Textarea";
import { useUserCreatePetForm } from "~/services/auth";
import { useCreatePet } from "~/services/customer/myPage";
import { transformKeyValueArrayToOptions } from "%/dataTransform";
import { ValidateControllerSelect } from "@/ControllerSelect";
import { Dialog } from "@/Dialog";
import useMyPageMain from "^/customer/myPage/useMyPageMain";
import { useLoadingStore } from "&/loading";
import useHideNav from "^/common/useHideNav";
import {
  canUseResourceBridge,
  uploadResourceViaBridge,
} from "%/appResourceUpload";

export const Route = createFileRoute("/customer/layout/user-pet-info-add")({
  component: RouteComponent,
});

interface PetInfoForm {
  petImg: string | File;
  petName: string;
  petRegNumber: string;
  petType: string;
  gender: string;
  year: string;
  month: string;
  day: string;
  petWeight: string;
  neuterYn: string;
  inoculation: string;
  personality: string;
  allergy: string;
  memo: string;
  userName: string;
  userSeq: number;
}

function RouteComponent() {
  useHideNav();
  const { history } = useRouter();
  const { mutate: createPet } = useCreatePet();
  const { data: petFormData } = useUserCreatePetForm();
  const { mypageMain } = useMyPageMain();
  const { toggleLoading } = useLoadingStore();
  console.log(mypageMain);
  // 모달 상태 관리
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState({
    title: "알림",
    message1: "",
    message2: "",
    message3: "",
  });
  const [showValidationErrorDialog, setShowValidationErrorDialog] =
    useState(false);
  const [validationErrorMessage, setValidationErrorMessage] = useState("");

  const showModal = (
    message1: string,
    message2: string = "",
    message3: string = "",
    title: string = "알림"
  ) => {
    setModalMessage({ title, message1, message2, message3 });
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    history.back();
  };

  // 앱(웹뷰)에서는 네이티브 브릿지로 이미지를 먼저 업로드 → fileSeq 사용 (구버전 WebView 업로드 실패 우회)
  const useBridge = canUseResourceBridge();
  const [bridgeFileSeq, setBridgeFileSeq] = useState<number | null>(null);
  const [bridgePreview, setBridgePreview] = useState<string>("");
  const [bridgeUploading, setBridgeUploading] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { isValid, errors },
    watch,
    register,
  } = useForm<PetInfoForm>({
    defaultValues: {
      petImg: "",
      petName: "",
      petRegNumber: "",
      petType: "",
      gender: "",
      year: "",
      month: "",
      day: "",
      petWeight: "",
      neuterYn: "",
      inoculation: "",
      personality: "",
      allergy: "",
      memo: "",
      userName: mypageMain?.userName || "",
      userSeq: 0,
    },
    mode: "onChange",
  });

  const bindSubmit = async (data: PetInfoForm) => {
    console.log("Pet form data:", data);
    console.log(petFormData);

    // 반려동물 사진은 선택 항목 — 없어도 등록 가능

    // 로딩 시작
    toggleLoading(true);

    // 생년월일 포맷 변경 (YYYY-MM-DD)
    const petBirthday = `${data.year}${data.month.padStart(2, "0")}${data.day.padStart(2, "0")}`;

    // 선택된 접종 정보 처리 (pet.tsx 패턴 참고)
    const inoculation = petFormData?.inoculations.map((item) => {
      if (data.inoculation.includes(item.cdInoculation)) {
        return {
          ...item,
          activeYn: "Y",
        };
      } else {
        return {
          ...item,
          activeYn: "N",
        };
      }
    });

    // 선택된 성격 정보 처리 (pet.tsx 패턴 참고)
    const personality = petFormData?.personalities.map((item) => {
      if (data.personality.includes(item.cdPersonality)) {
        return {
          ...item,
          activeYn: "Y",
        };
      } else {
        return {
          ...item,
          activeYn: "N",
        };
      }
    });

    // CreatePetRequest 형식에 맞게 데이터 변환
    const petCreateData = {
      data: {
        userName: data.userName,
        userSeq: data.userSeq,
        petName: data.petName,
        petRegNumber: data.petRegNumber,
        cdPetBreed: data.petType,
        cdPetGender: data.gender,
        petBirthday: petBirthday,
        // 앱 브릿지로 먼저 올린 경우 그 fileSeq 사용, 아니면 0(서버가 file 파트로 처리)
        fileSeq: useBridge && bridgeFileSeq ? bridgeFileSeq : 0,
        petWeight: parseFloat(data.petWeight) || 0,
        neuterYn: data.neuterYn,
        inoculations: inoculation,
        personalities: personality,
        allergy: data.allergy,
        memo: data.memo,
      },
      // 브릿지 경로에서는 파일을 다시 올리지 않는다(fileSeq 로 전달됨)
      file: useBridge && bridgeFileSeq ? undefined : data.petImg,
    };

    createPet(petCreateData, {
      onSuccess: (response) => {
        // 로딩 종료
        toggleLoading(false);
        if (response.statusCode) {
          showModal(
            "반려동물 정보가 성공적으로 추가되었습니다.",
            "",
            "",
            "저장 완료"
          );
        }
      },
      onError: (error) => {
        // 로딩 종료
        toggleLoading(false);
        console.error("펫 생성 실패:", error);
        showModal(
          "반려동물 정보 추가 중 오류가 발생했습니다.",
          "다시 시도해주세요.",
          "",
          "오류"
        );
      },
    });
  };

  // 연도 선택 옵션 생성 (현재 연도부터 20년 전까지)
  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 20 }, (_, i) => currentYear - i);
  }, []);

  // 월 선택 옵션 생성 (1-12월)
  const monthOptions = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => i + 1);
  }, []);

  // 일 선택 옵션 생성 (1-31일)
  const dayOptions = useMemo(() => {
    const selectedYear = parseInt(watch("year"));
    const selectedMonth = parseInt(watch("month"));

    if (!selectedYear || !selectedMonth)
      return Array.from({ length: 31 }, (_, i) => i + 1);

    const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
    return Array.from({ length: lastDay }, (_, i) => i + 1);
  }, [watch("year"), watch("month")]);

  // API 데이터를 옵션 형태로 변환
  const petBreedOptions = useMemo(() => {
    if (!petFormData?.petBreeds) return [];
    return transformKeyValueArrayToOptions(petFormData.petBreeds);
  }, [petFormData?.petBreeds]);

  const personalityOptions = useMemo(() => {
    if (!petFormData?.personalities) return [];
    return petFormData.personalities.map((item) => ({
      label: item.personalityName,
      value: item.cdPersonality,
    }));
  }, [petFormData?.personalities]);

  const inoculationOptions = useMemo(() => {
    if (!petFormData?.inoculations) return [];
    return petFormData.inoculations.map((item) => ({
      label: item.inoculationName,
      value: item.cdInoculation,
    }));
  }, [petFormData?.inoculations]);

  return (
    <>
      <LayoutHeader title={"계정정보 변경"} hasBackBtn hasFootPrint={false} />
      {/* 수신자 */}
      <div className="flex flex-col flex-auto bg-[#F5F5F5]">
        <div className="flex justify-between px-5 pt-3">
          <div className="text-[16px] font-semibold">반려동물 추가</div>
        </div>
        <form
          onSubmit={handleSubmit(bindSubmit)}
          className="flex flex-col flex-auto gap-4 px-5 pt-2"
        >
          <SequentialAnimation>
            <ValidateInput
              {...register("userName")}
              className="w-full"
              label="보호자 이름"
              disabled
              value={mypageMain?.userName || ""}
            />
            {useBridge ? (
              <div className="w-full">
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  반려동물 사진 등록 (선택)
                </label>
                <button
                  type="button"
                  disabled={bridgeUploading}
                  onClick={async () => {
                    try {
                      setBridgeUploading(true);
                      const r = await uploadResourceViaBridge(
                        "/api/user/mypage/upload-image"
                      );
                      setBridgeFileSeq(r.fileSeq);
                      setBridgePreview(r.fileUrl);
                    } catch (e) {
                      setValidationErrorMessage(
                        e instanceof Error
                          ? e.message
                          : "사진 업로드에 실패했습니다."
                      );
                      setShowValidationErrorDialog(true);
                    } finally {
                      setBridgeUploading(false);
                    }
                  }}
                  className="flex h-44 w-full items-center justify-center overflow-hidden rounded-lg border border-dashed border-gray-300 bg-gray-50 disabled:opacity-60"
                >
                  {bridgePreview ? (
                    <img
                      src={bridgePreview}
                      alt="반려동물 사진"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-sm text-gray-500">
                      {bridgeUploading ? "업로드 중..." : "사진 선택하기"}
                    </span>
                  )}
                </button>
              </div>
            ) : (
              <Controller
                name="petImg"
                control={control}
                render={({ field }) => (
                  <ValidateUploader
                    onUploaded={field.onChange}
                    onValidationError={(error) => {
                      setValidationErrorMessage(error);
                      setShowValidationErrorDialog(true);
                    }}
                    errorMessage={errors?.petImg?.message}
                    className="w-full"
                    label="반려동물 사진 등록 (선택)"
                    maxSizeMB={10}
                  />
                )}
              />
            )}
            <Controller
              name="petName"
              control={control}
              rules={{ required: "반려동물 이름을 입력하세요" }}
              render={({ field }) => (
                <ValidateInput
                  {...field}
                  className="w-full"
                  label="반려동물 이름"
                  errorMessage={errors?.petName?.message}
                />
              )}
            />
            <Controller
              name="petRegNumber"
              control={control}
              render={({ field }) => (
                <ValidateInput
                  {...field}
                  className="w-full"
                  label="국가동물등록번호(선택)"
                />
              )}
            />
            <Controller
              name="petType"
              control={control}
              rules={{ required: "반려동물 품종을 선택하세요" }}
              render={({ field }) => (
                <ValidateSelectList
                  onChange={(selectedOption) =>
                    field.onChange(selectedOption?.value || "")
                  }
                  label="반려동물 품종"
                  placeholder="품종을 선택하세요"
                  required={true}
                  isSearchable={true}
                  errorMessage={errors?.petType?.message}
                  options={petBreedOptions}
                />
              )}
            />
            <Controller
              name="gender"
              control={control}
              rules={{ required: "성별을 선택하세요" }}
              render={({ field }) => (
                <ValidateControllerSelect
                  value={field.value ? field.value.split(",") : []}
                  onChange={(value) => {
                    field.onChange(value.join(","));
                  }}
                  label="성별"
                  required={true}
                  max={1}
                  col={2}
                  errorMessage={errors?.gender?.message}
                  options={[
                    { label: "암컷", value: "FEMALE" },
                    { label: "수컷", value: "MALE" },
                  ]}
                />
              )}
            />
            <div>
              <label className="block mb-2 font-medium text-gray-900">
                반려동물 나이 <span className="text-orange-500">*</span>
              </label>
              <div className="flex gap-2 w-full">
                <Controller
                  name="year"
                  control={control}
                  rules={{ required: "연도를 입력하세요" }}
                  render={({ field }) => (
                    <ValidateSelectList
                      onChange={(selectedOption) =>
                        field.onChange(selectedOption?.value || "")
                      }
                      isDefaultValue={false}
                      placeholder="년도"
                      isSearchable={false}
                      options={yearOptions.map((year) => ({
                        value: String(year),
                        label: `${year}년`,
                      }))}
                      className="h-14 text-lg text-center"
                    />
                  )}
                />
                <Controller
                  name="month"
                  control={control}
                  rules={{ required: "월을 입력하세요" }}
                  render={({ field }) => (
                    <ValidateSelectList
                      onChange={(selectedOption) =>
                        field.onChange(selectedOption?.value || "")
                      }
                      isDefaultValue={false}
                      placeholder="00월"
                      isSearchable={false}
                      options={monthOptions.map((month) => ({
                        value: month.toString().padStart(2, "0"),
                        label: `${month}월`,
                      }))}
                      className="h-14 text-lg text-center"
                    />
                  )}
                />
                <Controller
                  name="day"
                  control={control}
                  rules={{ required: "일을 입력하세요" }}
                  render={({ field }) => (
                    <ValidateSelectList
                      onChange={(selectedOption) =>
                        field.onChange(selectedOption?.value || "")
                      }
                      isDefaultValue={false}
                      placeholder="00일"
                      isSearchable={false}
                      options={dayOptions.map((day) => ({
                        value: String(day),
                        label: `${day.toString().padStart(2, "0")}일`,
                      }))}
                      className="h-14 text-lg text-center"
                    />
                  )}
                />
              </div>
            </div>

            <div className="relative pr-6 w-fit">
              <Controller
                name="petWeight"
                control={control}
                rules={{ required: "반려동물 몸무게를 입력하세요" }}
                render={({ field }) => (
                  <ValidateInput
                    {...field}
                    type="number"
                    inputMode="decimal"
                    label="반려동물 몸무게"
                    required={true}
                    errorMessage={errors?.petWeight?.message}
                  />
                )}
              />
              <span className="absolute right-0 bottom-0">kg</span>
            </div>
            <Controller
              name="neuterYn"
              control={control}
              rules={{ required: "중성화 여부를 선택하세요" }}
              render={({ field }) => (
                <ValidateControllerSelect
                  value={field.value ? field.value.split(",") : []}
                  onChange={(value) => {
                    field.onChange(value.join(","));
                  }}
                  label="중성화"
                  required={true}
                  col={2}
                  max={1}
                  errorMessage={errors?.neuterYn?.message}
                  options={[
                    { label: "중성화 완료", value: "Y" },
                    { label: "중성화 예정/미완료", value: "N" },
                  ]}
                />
              )}
            />
            <Controller
              name="inoculation"
              control={control}
              rules={{ required: "접종 정보를 선택하세요" }}
              render={({ field }) => (
                <ValidateControllerSelect
                  value={field.value ? field.value.split(",") : []}
                  onChange={(value) => {
                    field.onChange(value.join(","));
                  }}
                  label="최근 1년 이내 접종 정보"
                  required={true}
                  errorMessage={errors?.inoculation?.message}
                  options={inoculationOptions}
                />
              )}
            />
            <Controller
              name="personality"
              control={control}
              rules={{ required: "성격/성향을 선택하세요" }}
              render={({ field }) => (
                <ValidateControllerSelect
                  value={field.value ? field.value.split(",") : []}
                  onChange={(value) => {
                    field.onChange(value.join(","));
                  }}
                  label="해당하는 성격/성향을 모두 선택해주세요"
                  required={true}
                  errorMessage={errors?.personality?.message}
                  options={personalityOptions}
                />
              )}
            />
            <Controller
              name="allergy"
              control={control}
              render={({ field }) => (
                <ValidateTextarea
                  {...field}
                  label="알레르기 정보를 알려주세요"
                />
              )}
            />
            <Controller
              name="memo"
              control={control}
              render={({ field }) => (
                <ValidateTextarea {...field} label="기타 메모" />
              )}
            />
          </SequentialAnimation>
        </form>
        <div className="px-4 py-6 w-full bg-[#F5F5F5]">
          <Button
            className="w-full bg-[#F5F5F5]"
            style={{ pointerEvents: "auto" }}
            onClick={handleSubmit(bindSubmit)}
            disabled={!isValid}
          >
            <div className="py-3 mx-auto w-full text-base font-semibold text-center text-white bg-orange-400 rounded-full">
              반려동물 정보 저장하기
            </div>
          </Button>
        </div>
      </div>

      {/* 저장 결과 메시지 모달 */}
      <Dialog
        visible={isModalVisible}
        onClose={handleModalClose}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="flex flex-col mb-2">
            <div className="text-lg font-semibold text-center">
              {modalMessage.title}
            </div>
          </div>
          <div className="flex flex-col mb-2">
            <div className="text-sm text-center text-[#666666]">
              {modalMessage.message1}
            </div>
            {modalMessage.message2 && (
              <div className="text-sm text-center text-[#666666]">
                {modalMessage.message2}
              </div>
            )}
            {modalMessage.message3 && (
              <div className="text-sm text-center text-[#666666]">
                {modalMessage.message3}
              </div>
            )}
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
              onClick={handleModalClose}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>

      {/* 파일 검증 에러 Dialog */}
      <Dialog
        visible={showValidationErrorDialog}
        onClose={() => setShowValidationErrorDialog(false)}
      >
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">파일 형식 오류</h3>
          <p className="mb-4 text-gray-600">{validationErrorMessage}</p>
          <p className="mb-4 text-sm text-gray-500">
            이미지 파일만 업로드 가능합니다. (JPG, PNG, GIF, WEBP, BMP, SVG)
          </p>
          <button
            onClick={() => setShowValidationErrorDialog(false)}
            className="px-4 py-2 text-white bg-orange-500 rounded-lg transition-colors hover:bg-orange-600"
          >
            확인
          </button>
        </div>
      </Dialog>
    </>
  );
}
